import { usePlaySound } from "common/utils";
import i18n from "features/parent/i18n";
import { NativePackagesStatus } from "features/subscription";
import { PlanCategory, BillingPeriod } from "features/subscription/useNativePackages";
import { PLAN_CATEGORY_COLORS } from "./subscriptionTabUtils";

interface UpgradeSectionProps {
  billingPeriod: BillingPeriod;
  packagesStatus: NativePackagesStatus;
  isNativeApp: boolean;
  onBillingPeriodChange: (period: BillingPeriod) => void;
  onPurchase: (planCategory: PlanCategory) => void;
  getPriceDisplay: (
    planCategory: PlanCategory,
    period: BillingPeriod,
  ) => { price: number; priceString: string };
}

interface PlanCardProps {
  planCategory: PlanCategory;
  billingPeriod: BillingPeriod;
  getPriceDisplay: (
    planCategory: PlanCategory,
    period: BillingPeriod,
  ) => { price: number; priceString: string };
  onPurchase: (planCategory: PlanCategory) => void;
}

function PlanCard({ planCategory, billingPeriod, getPriceDisplay, onPurchase }: PlanCardProps) {
  const priceInfo = getPriceDisplay(planCategory, billingPeriod);
  const planName =
    planCategory === "standard"
      ? i18n.t("MSG_PLAN_STANDARD_FULL_PARENT")
      : i18n.t("MSG_PLAN_PREMIUM_FULL_PARENT");

  // 基本プランのみ割引表示（20% OFF: 500円 → 400円）
  const showDiscount = planCategory === "standard";
  // 元の価格を計算
  // 月額: 現在価格 / 0.8 (20% OFF)
  // 年額: 月額価格 × 10
  const originalPrice =
    billingPeriod === "annual"
      ? Math.round(getPriceDisplay(planCategory, "monthly").price / 0.8) * 10
      : Math.round(priceInfo.price / 0.8);

  return (
    <div className="flex-1 flex-col-el items-center p-6 bg-white rounded-[16px] border-4 border-transparent">
      <div
        className="text-[30px] text-white mb-4 px-4 py-1 rounded-full"
        style={{ backgroundColor: PLAN_CATEGORY_COLORS[planCategory] }}
      >
        {planName}
      </div>
      <div className="flex-row-el items-center gap-4 mb-6 h-[110px]">
        {showDiscount && (
          <div className="relative w-[110px] h-[110px] shrink-0">
            <div
              className="absolute inset-0 bg-[#f44336] flex-col-el items-center justify-center"
              style={{
                clipPath: `polygon(
                  50% 0%, 61% 15%, 79% 6%, 77% 25%, 98% 35%,
                  85% 50%, 98% 65%, 77% 75%, 79% 94%, 61% 85%,
                  50% 100%, 39% 85%, 21% 94%, 23% 75%, 2% 65%,
                  15% 50%, 2% 35%, 23% 25%, 21% 6%, 39% 15%
                )`,
              }}
            >
              <span className="text-white text-[12px]">家計支援</span>
              <span className="text-white text-[26px] leading-none">20%</span>
              <span className="text-white text-[16px]">OFF</span>
            </div>
          </div>
        )}
        <div className="flex-row-el items-baseline gap-2">
          {showDiscount && (
            <span className="text-[24px] text-gray2-100 line-through">
              ¥{originalPrice.toLocaleString()}
            </span>
          )}
          <span className="text-[48px]" style={{ color: PLAN_CATEGORY_COLORS[planCategory] }}>
            {priceInfo.priceString}
          </span>
          <span className="text-[24px] text-gray2-100">
            /{billingPeriod === "monthly" ? "月" : "年"}
          </span>
        </div>
      </div>
      <button
        className="flex-row-el items-center justify-center gap-2 py-3 px-6 text-white rounded-[12px] text-[24px] border-none cursor-pointer hover:opacity-90 transition-all"
        style={{ backgroundColor: PLAN_CATEGORY_COLORS[planCategory] }}
        onClick={() => onPurchase(planCategory)}
      >
        <span className="material-symbols-outlined text-[30px]">shopping_cart</span>
        {i18n.t("MSG_SUBSCRIPTION_PURCHASE_BTN")}
      </button>
    </div>
  );
}

export function UpgradeSection({
  billingPeriod,
  packagesStatus,
  isNativeApp,
  onBillingPeriodChange,
  onPurchase,
  getPriceDisplay,
}: UpgradeSectionProps) {
  const play = usePlaySound();

  return (
    <div className="bg-white rounded-[16px] p-6">
      <div className="flex-row-el items-center justify-between mb-5 pb-3 border-b border-outline">
        <div className="flex-row-el items-center gap-2 text-[27px] text-textcolor-black">
          <span className="material-symbols-outlined !text-[45px] text-red-100">upgrade</span>
          {i18n.t("MSG_PARENT_PAGE_UPGRADE_SECTION")}
        </div>
      </div>

      {isNativeApp && packagesStatus === NativePackagesStatus.LOADING ? (
        <div className="flex-col-el flex-center py-8">
          <div className="w-8 h-8 border-4 border-[#66BB6A] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          <div className="flex-row-el justify-center mb-6">
            <div className="flex-row-el bg-gray2-40 rounded-full p-1">
              <button
                className={`px-6 py-2 rounded-full text-[24px] border-none cursor-pointer transition-all ${
                  billingPeriod === "monthly"
                    ? "bg-white text-textcolor-black shadow-sm"
                    : "bg-transparent text-gray2-100"
                }`}
                onClick={() => {
                  play();
                  onBillingPeriodChange("monthly");
                }}
              >
                {i18n.t("MSG_SUBSCRIPTION_MONTHLY_TITLE")}
              </button>
              <button
                className={`relative px-6 py-2 rounded-full text-[24px] border-none cursor-pointer transition-all ${
                  billingPeriod === "annual"
                    ? "bg-white text-textcolor-black shadow-sm"
                    : "bg-transparent text-gray2-100"
                }`}
                onClick={() => {
                  play();
                  onBillingPeriodChange("annual");
                }}
              >
                {i18n.t("MSG_SUBSCRIPTION_ANNUAL_TITLE")}
                <span className="absolute -top-2 -right-2 px-2 py-0.5 bg-orange-100 rounded-full text-[14px] text-white whitespace-nowrap">
                  2ヶ月分お得！
                </span>
              </button>
            </div>
          </div>

          <div className="flex-row-el gap-4">
            <PlanCard
              planCategory="standard"
              billingPeriod={billingPeriod}
              getPriceDisplay={getPriceDisplay}
              onPurchase={onPurchase}
            />

            <PlanCard
              planCategory="premium"
              billingPeriod={billingPeriod}
              getPriceDisplay={getPriceDisplay}
              onPurchase={onPurchase}
            />
          </div>
        </>
      )}
    </div>
  );
}
